import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import img from "../../assets/images/view-glacier-night.jpg";
import img2 from "../../assets/images/hero-image-1.jpg";
import headingImg from "../../assets/images/logo/light-logo-half.png";
import TypingText from "../utils/TypingText";

export default function MountainLodgeHero() {
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const maskY = useTransform(scrollYProgress, [0, 0.5], [0, -600]);
  const maskScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.5]);
  const maskOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const contentY = useTransform(scrollYProgress, [0, 0.5], [0, -50]);
  const contentScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  return (
    <section ref={heroRef} className="relative w-full h-[200vh] overflow-hidden">
      {/* Background Layer */}
      <div className="absolute top-0 w-full h-[200vh] z-10">
        <img
          src={img2}
          alt="Mountain background"
          className="w-full h-[200vh] object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Masked Overlay Layer */}
      <motion.img
        src={img}
        style={{ y: maskY, scale: maskScale, opacity: maskOpacity }}
        className="absolute inset-0 w-screen h-screen object-cover pointer-events-none z-20"
      />

      {/* Hero Content */}
      <motion.div
        style={{ y: contentY, scale: contentScale }}
        className="sticky top-0 z-30 w-full h-screen flex flex-col items-center justify-center text-center text-white px-4 sm:px-6"
      >
        {/* Heading Image */}
        <img
          src={headingImg}
          alt="Heading Decorative"
          className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 object-contain mb-4 sm:mb-6 opacity-90 mix-blend-overlay drop-shadow-xl"
        />

        <div className="flex items-center justify-center">
          <div className="text-center leading-none select-none">
            <motion.h2
              whileInView={{ x: 0, opacity: 1 }}
              initial={{ x: 100, opacity: 0 }}
              transition={{ duration: 2, ease: "easeIn" }}
              viewport={{ once: true }}
              className="text-[70px] sm:text-[90px] md:text-[140px] font-bold text-white/90 tracking-tight"
            >
              BROADBOY
            </motion.h2>

            <motion.h2
              whileInView={{ x: 0, opacity: 1 }}
              initial={{ x: -100, opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              viewport={{ once: true }}
              className="text-[40px] sm:text-[60px] md:text-[90px] font-light text-[#10364a] -mt-6 sm:-mt-8 md:-mt-10 tracking-tight"
            >
              Adventures
            </motion.h2>
          </div>
        </div>

        {/* Subheading */}
        <h3 className="mt-4 sm:mt-6 text-sm sm:text-lg md:text-2xl tracking-[4px] sm:tracking-[6px] font-light opacity-90 mix-blend-overlay">
          <TypingText text="- YOUR PERFECT GETAWAY -" />
        </h3>
      </motion.div>

      {/* Bottom Overlay Content */}
      <div className="absolute inset-0 z-40 flex flex-col items-start justify-end p-6 sm:p-12 pointer-events-none">
        <h3
          className="
            font-bold leading-none select-none pointer-events-none
            text-gray-300/10

            text-[clamp(3.5rem,8vw,4.5rem)]
            sm:text-[clamp(4rem,10vw,6rem)]
            md:text-[clamp(5rem,14vw,12.5rem)]
          "
        >
          <span className="block md:inline">EXPLORE </span>
          <span className="block md:inline">THE </span>
          <span className="block md:inline">MOUNTAINS</span>
        </h3>


        <div className="mt-4 max-w-full sm:max-w-md text-left pointer-events-auto">
          <p className="text-gray-200 uppercase tracking-widest mb-1 text-sm sm:text-base">TRAVEL</p>
          <h2 className="text-white text-2xl sm:text-3xl md:text-5xl font-bold mb-2">Get inspired</h2>
          <p className="text-gray-300 mb-4 text-sm sm:text-base">
            Discover breathtaking landscapes, thrilling outdoor experiences, and unforgettable moments. 
            Let our adventures inspire your next journey into nature.
          </p>
          <a
            href="./adventures/expeditions"
            className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-full border border-white text-white font-medium hover:bg-white hover:text-black transition text-sm sm:text-base"
          >
            DISCOVER
          </a>
        </div>
      </div>
    </section>
  );
}
